<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import EmojiPicker from 'shared/components/emoji/EmojiPicker.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ReactionsAPI from './api';
import { QUICK_REACTIONS, groupReactions, myReaction } from './helpers';

const props = defineProps({
  messageId: { type: Number, required: true },
  conversationId: { type: Number, required: true },
  reactions: { type: Array, default: () => [] },
  canReact: { type: Boolean, default: false },
  alignRight: { type: Boolean, default: false },
});

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');

const isPickerOpen = ref(false);
const showFullPicker = ref(false);
const isSaving = ref(false);

const currentUserId = computed(() => currentUser.value?.id);
const groups = computed(() =>
  groupReactions(props.reactions, currentUserId.value)
);
const mine = computed(() => myReaction(props.reactions, currentUserId.value));

const closePicker = () => {
  isPickerOpen.value = false;
  showFullPicker.value = false;
};

const react = async emoji => {
  if (!props.canReact || isSaving.value) return;
  // clicking my own reaction removes it
  const next = emoji === mine.value ? '' : emoji;
  isSaving.value = true;
  try {
    await ReactionsAPI.react(props.conversationId, props.messageId, next);
  } catch (error) {
    useAlert(error?.response?.data?.error || t('WASAPI_REACTIONS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
    closePicker();
  }
};

const onEmojiSelected = ({ emoji }) => react(emoji);

const tooltipFor = group => group.names.filter(Boolean).join(', ');
</script>

<template>
  <div
    v-if="groups.length || canReact"
    class="relative flex items-center gap-1 mt-0.5 flex-wrap group/reactions"
    :class="alignRight ? 'justify-end' : 'justify-start'"
  >
    <button
      v-for="group in groups"
      :key="group.emoji"
      v-tooltip.top="tooltipFor(group)"
      type="button"
      class="flex items-center gap-1 px-1.5 h-6 text-xs rounded-full outline outline-1 transition-colors"
      :class="
        group.mine
          ? 'bg-n-brand/10 outline-n-brand/40 text-n-slate-12'
          : 'bg-n-alpha-1 outline-n-weak text-n-slate-11 hover:outline-n-slate-7'
      "
      :disabled="!canReact"
      @click="react(group.emoji)"
    >
      <span class="text-sm leading-none">{{ group.emoji }}</span>
      <span v-if="group.count > 1">{{ group.count }}</span>
    </button>

    <OnClickOutside v-if="canReact" @trigger="closePicker">
      <div class="relative">
        <button
          v-tooltip.top="t('WASAPI_REACTIONS.ADD')"
          type="button"
          class="grid h-6 w-6 place-content-center rounded-full text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-opacity"
          :class="
            groups.length || isPickerOpen
              ? 'opacity-100'
              : 'opacity-0 group-hover/reactions:opacity-100 [.message-bubble-container:hover_&]:opacity-100'
          "
          :aria-label="t('WASAPI_REACTIONS.ADD')"
          @click="isPickerOpen = !isPickerOpen"
        >
          <Icon icon="i-lucide-smile-plus" class="size-4" />
        </button>

        <div
          v-if="isPickerOpen && !showFullPicker"
          class="absolute z-20 flex items-center gap-1 p-1 mt-1 rounded-full shadow-lg bg-n-surface-2 outline outline-1 outline-n-weak"
          :class="alignRight ? 'right-0' : 'left-0'"
        >
          <button
            v-for="emoji in QUICK_REACTIONS"
            :key="emoji"
            type="button"
            class="grid h-8 w-8 place-content-center text-lg rounded-full hover:bg-n-alpha-2 hover:scale-110 transition-transform"
            :class="{ 'bg-n-brand/10': emoji === mine }"
            @click="react(emoji)"
          >
            {{ emoji }}
          </button>
          <button
            type="button"
            class="grid h-8 w-8 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
            :aria-label="t('WASAPI_REACTIONS.MORE')"
            @click="showFullPicker = true"
          >
            <Icon icon="i-lucide-plus" class="size-4" />
          </button>
        </div>
        <EmojiPicker
          v-if="isPickerOpen && showFullPicker"
          class="mt-1"
          :class="alignRight ? 'right-0' : 'left-0'"
          @select="onEmojiSelected"
        />
      </div>
    </OnClickOutside>
  </div>
</template>
